/** @jsxImportSource @emotion/react */
import {
  createStyles,
  Input,
  Select,
  Typography,
} from '@quickbit/qb-design-system';
import { useState } from 'react';

const PRESET_COMMENTS = [
  { value: 'Wrong birth year', label: 'Wrong birth year' },
  { value: 'Inactive PEP/RCA status', label: 'Inactive PEP/RCA status' },
  { value: 'No match on full name', label: 'No match on full name' },
  { value: 'FREE_TEXT', label: 'Free text' },
];

interface MatchCommentEditorProps {
  comment: string;
  onSave: (comment: string) => void;
}

export const MatchCommentEditor = ({
  comment,
  onSave,
}: MatchCommentEditorProps) => {
  const styles = useStyles();
  const [editing, setEditing] = useState(false);
  const [freeText, setFreeText] = useState(false);
  const [freeTextValue, setFreeTextValue] = useState('');

  const handleSelect = (values: string[]) => {
    const selected = values[0];
    if (!selected) return;

    if (selected === 'FREE_TEXT') {
      setFreeText(true);
      setFreeTextValue('');
      return;
    }

    setEditing(false);
    onSave(selected);
  };

  const handleFreeTextSubmit = () => {
    if (!freeTextValue.trim()) return;
    setFreeText(false);
    setEditing(false);
    onSave(freeTextValue.trim());
  };

  if (!editing) {
    return (
      <div
        css={styles.commentDisplay}
        onClick={() => {
          setEditing(true);
          const isPreset = PRESET_COMMENTS.some(
            (opt) => opt.value === comment && opt.value !== 'FREE_TEXT'
          );
          if (comment && !isPreset) {
            setFreeText(true);
            setFreeTextValue(comment);
          } else {
            setFreeText(false);
          }
        }}
      >
        <Typography>{comment || 'No comment'}</Typography>
      </div>
    );
  }

  if (freeText) {
    return (
      <div css={styles.freeTextContainer}>
        <Input
          type="text"
          label="Enter comment"
          value={freeTextValue}
          onChange={(e) => setFreeTextValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleFreeTextSubmit();
            if (e.key === 'Escape') {
              setFreeText(false);
              setEditing(false);
            }
          }}
          onBlur={handleFreeTextSubmit}
        />
      </div>
    );
  }

  return (
    <div css={styles.selectContainer}>
      <Select
        label="Comment"
        options={PRESET_COMMENTS}
        values={
          PRESET_COMMENTS.some((opt) => opt.value === comment)
            ? [comment]
            : comment
            ? ['FREE_TEXT']
            : []
        }
        onSelect={handleSelect}
      />
    </div>
  );
};

const useStyles = createStyles(({ spacing }) => ({
  commentDisplay: {
    padding: `${spacing.xxxs} 0`,
  },
  clickable: {
    cursor: 'pointer',
    '&:hover': {
      opacity: 0.7,
    },
  },
  selectContainer: {
    minWidth: 200,
    zIndex: 999,
  },
  freeTextContainer: {
    minWidth: 200,
  },
}));
